/**
 * LandingPage - StackRanked Homepage
 * 
 * Dark minimal design with feature showcase sections
 * "The First Platform That Actually Loves Product Teams."
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Sparkles,
  Menu,
  X
} from 'lucide-react';
import MuxPlayer from '@mux/mux-player-react';
import PricingSection from '../components/pricing/PricingSection';
import { initSiteAnalytics } from '../utils/siteAnalytics';
import api from '../services/api';

// Integration logos as simple text for the ticker
const INTEGRATIONS = ['Linear', 'Jira', 'GitHub', 'Notion', 'Slack'];

// Mux Video IDs
const MUX_VIDEOS = {
  PRODUCTIVITY: 'r7FGBSwAxPkons5GoAhtjetB100TYdrh4YuUsifDyLNg',
  LESS_EMAIL: 'B5c1RVHgbiefntYULj2QExIipMViHg4GAPiINB1YJLI',
  FEWER_MEETINGS: 'LUyZLy27lHK9xSjLtAKyp01estj8Ow26DK8fWyNTvoKA'
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [askInput, setAskInput] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Initialize site analytics on landing page
  useEffect(() => {
    const cleanup = initSiteAnalytics();
    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // Fetch public feature flags
  useEffect(() => {
    const fetchPublicFlags = async () => {
      try {
        const response = await api.get('/api/feature-flags/public');
        setDemoEnabled(response.data.flags?.demo_enabled || false);
      } catch (err) {
        console.error('Failed to fetch public flags:', err);
        setDemoEnabled(false);
      }
    };
    fetchPublicFlags();
  }, []);

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans selection:bg-purple-500 selection:text-white overflow-x-hidden">
      
      {/* Compact Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-black/80 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <span className="text-white font-bold text-sm">SR</span>
            </div>
            <span className="text-lg font-semibold text-white tracking-tight">
              StackRanked.ai
            </span>
          </div>

          {/* Nav Links — desktop */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/features" className="text-sm text-gray-400 hover:text-white transition-colors">
              Features
            </Link>
            <a href="#pricing" className="text-sm text-gray-400 hover:text-white transition-colors">
              Pricing
            </a>
            <a href="/blog" className="text-sm text-gray-400 hover:text-white transition-colors">
              Blog
            </a>
            {demoEnabled && (
              <a href="#demo" className="text-sm text-gray-400 hover:text-white transition-colors">
                Demo
              </a>
            )}
          </div>

          {/* Auth Buttons — desktop */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-gray-400 hover:text-white hover:bg-white/5"
            >
              Sign In
            </Button>
            <Button
              onClick={() => {
                const pricingEl = document.getElementById('pricing');
                if (pricingEl) {
                  pricingEl.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-white text-black hover:bg-gray-200 font-medium"
            >
              Get Started
            </Button>
          </div>

          {/* Hamburger — mobile */}
          <button
            className="md:hidden text-gray-400 hover:text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 bg-black/95 backdrop-blur-md px-6 py-4 space-y-3">
            <Link to="/features" className="block text-sm text-gray-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
              Features
            </Link>
            <a href="#pricing" className="block text-sm text-gray-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
              Pricing
            </a>
            <a href="/blog" className="block text-sm text-gray-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
              Blog
            </a>
            {demoEnabled && (
              <a href="#demo" className="block text-sm text-gray-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
                Demo
              </a>
            )}
            <div className="border-t border-white/5 pt-3 space-y-0">
              <Link to="/login" className="block text-sm text-gray-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
                Sign In
              </Link>
              <a href="#pricing" className="block text-sm text-gray-400 hover:text-white transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
                Get Started
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-[1200px] mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-8">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-purple-300 font-medium">Product Management for the AI-Native</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 leading-[1.1]">
            <span className="text-white">The First Platform That Actually</span>
            <br />
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
              Loves Product Teams.
            </span>
          </h1>

          {/* Subhead */}
          <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed">
            Transform your upstream strategy into downstream reality. By automating the technical heavy lifting of spec-writing and market research, StackRanked frees your team to build with intent, connecting every feature back to the market needs that define success.
          </p>

          {/* CTA Buttons */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <Button 
              size="lg"
              onClick={() => {
                const pricingEl = document.getElementById('pricing');
                if (pricingEl) {
                  pricingEl.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-white text-black hover:bg-gray-200 px-8 py-6 text-base font-medium"
            >
              Start Free Trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            {demoEnabled && (
              <Button 
                size="lg"
                variant="outline"
                onClick={() => {
                  const demoEl = document.getElementById('demo');
                  if (demoEl) {
                    demoEl.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="border-white/20 text-white hover:bg-white/5 px-8 py-6 text-base"
              >
                See Demo
              </Button>
            )}
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-12 text-center">
            <div>
              <p className="text-3xl font-bold text-white">10x</p>
              <p className="text-sm text-gray-500">Productivity</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">33%</p>
              <p className="text-sm text-gray-500">Less Email</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">25%</p>
              <p className="text-sm text-gray-500">Fewer Meetings</p>
            </div>
          </div>
        </div>
      </section>

      {/* Minimal Ticker */}
      <div className="border-y border-white/10 bg-black/50 backdrop-blur-sm py-4">
        <div className="max-w-[1400px] mx-auto px-6 flex justify-between items-center">
          <span className="text-[10px] font-mono uppercase tracking-widest text-gray-400">
            Trusted Integrations
          </span>
          <div className="flex gap-8">
            {INTEGRATIONS.map((name) => (
              <span 
                key={name} 
                className="text-[10px] font-mono uppercase tracking-widest text-gray-300 hover:text-white transition-colors cursor-default"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* FEATURE SHOWCASE SECTIONS (Cursor-style)    */}
      {/* ============================================ */}

      {/* Section 1: 10x Productivity - Text Left / Video Right */}
      <section id="productivity" className="relative py-32 overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-950/20 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-purple-900/10 to-transparent pointer-events-none" />
        
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Text Content - Left */}
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                <span className="text-white">Ideas to dev-ready specs</span>
                <br />
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  in minutes not hours or days
                </span>
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed max-w-lg">
                StackRanked's AI, infused with your product's context and a deep understanding 
                of product management best practices, quickly refines your insights into 
                detailed epics and dev-ready requirements - letting you focus on the "why" 
                instead of the "how." You remain the visionary, refining the final output 
                with your unique instinct rather than drowning in detailed documentation.
              </p>
              <Link 
                to="/features#features"
                className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-medium transition-colors"
              >
                Learn about Features & AI Co-Pilots
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Video - Right (bleeds out 15%) */}
            <div className="relative lg:w-[115%]">
              <div className="relative rounded-xl overflow-hidden bg-[#0a0a0a] border border-white/10 shadow-2xl shadow-purple-500/10">
                {/* Video Container */}
                <div className="aspect-video bg-gradient-to-br from-[#111] to-[#0a0a0a]">
                  {MUX_VIDEOS.PRODUCTIVITY ? (
                    <MuxPlayer
                      data-testid="video-productivity"
                      playbackId={MUX_VIDEOS.PRODUCTIVITY}
                      metadata={{
                        video_title: "AI Co-Pilot Demo",
                        viewer_user_id: "landing-page-visitor"
                      }}
                      streamType="on-demand"
                      autoPlay="muted"
                      loop
                      muted
                      style={{
                        width: '100%',
                        height: '100%',
                        '--controls': 'none',
                        '--media-object-fit': 'cover'
                      }}
                    />
                  ) : (
                    <div 
                      data-testid="video-placeholder-productivity"
                      className="w-full h-full flex flex-col items-center justify-center text-gray-500"
                    >
                      <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                      <p className="text-sm font-mono">MUX_VIDEO_PRODUCTIVITY</p>
                      <p className="text-xs text-gray-600 mt-1">AI Co-Pilot Demo</p>
                    </div>
                  )}
                </div>
              </div>
              {/* Subtle glow effect */}
              <div className="absolute -inset-4 bg-purple-500/5 blur-3xl rounded-full pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: 33% Less Email - Text Right / Video Left */}
      <section id="less-email" className="relative py-32 overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-bl from-emerald-950/20 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-emerald-900/10 to-transparent pointer-events-none" />
        
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Video - Left (bleeds out 15%) */}
            <div className="relative order-2 lg:order-1 lg:w-[115%] lg:-ml-[15%]">
              <div className="relative rounded-xl overflow-hidden bg-[#0a0a0a] border border-white/10 shadow-2xl shadow-emerald-500/10">
                {/* Video Container */}
                <div className="aspect-video bg-gradient-to-br from-[#111] to-[#0a0a0a] flex items-center justify-center">
                  {/* Mux Video Placeholder */}
                  {MUX_VIDEOS.LESS_EMAIL ? (
                    <MuxPlayer
                      data-testid="video-less-email"
                      playbackId={MUX_VIDEOS.LESS_EMAIL}
                      metadata={{
                        video_title: "Contextual Notes Demo",
                        viewer_user_id: "landing-page-visitor"
                      }}
                      streamType="on-demand"
                      autoPlay="muted"
                      loop
                      muted
                      style={{
                        width: '100%',
                        height: '100%',
                        '--controls': 'none',
                        '--media-object-fit': 'cover'
                      }}
                    />
                  ) : (
                    <div 
                      data-testid="video-placeholder-email"
                      className="w-full h-full flex flex-col items-center justify-center text-gray-500"
                    >
                      <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                      <p className="text-sm font-mono">MUX_VIDEO_EMAIL</p>
                      <p className="text-xs text-gray-600 mt-1">Contextual Notes Demo</p>
                    </div>
                  )}
                </div>
              </div>
              {/* Subtle glow effect */}
              <div className="absolute -inset-4 bg-emerald-500/5 blur-3xl rounded-full pointer-events-none" />
            </div>

            {/* Text Content - Right */}
            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                <span className="text-white">More thinking,</span>
                <br />
                <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                  less typing
                </span>
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed max-w-lg">
                Product vision, persona needs, epic context, feature details, and requirements 
                automatically flow into the developer's AI coding assistant via MCP & Ticketing 
                solution. Commits, PRs, and status updates flow back through GitHub. Questions 
                happen on the feature itself, not in a side channel. You write the spec 
                once - everything else moves automatically. No out-of-control email threads. 
                No copy-pasting specs into tickets. No status meetings.
              </p>
              <Link 
                to="/features#requests"
                className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-medium transition-colors"
              >
                Learn about Requests & Triage
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: 25% Fewer Meetings - Text Left / Video Right */}
      <section id="fewer-meetings" className="relative py-32 overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-950/20 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-violet-900/10 to-transparent pointer-events-none" />
        
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Text Content - Left */}
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                <span className="text-white">Dashboards show the what.</span>
                <br />
                <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                  AI tells you the why.
                </span>
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed max-w-lg">
                Every product tool has dashboards. So do we. But dashboards tell 
                you <em>2 features are blocked</em> - not which ones, not why, not whether 
                your KPIs are at risk. That answer usually takes three tabs, two Slack 
                threads, and a meeting. With StackRanked's Executive AI, you ask: 
                "What's blocked and what's the downstream impact?" You get the answer in 
                seconds - with every feature, goal, and initiative linked directly, one 
                click to inspect. Need a status update for a stakeholder about their 
                initiative? Ask, copy, send. Done.
              </p>
              <Link 
                to="/features#ai-executive"
                className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-medium transition-colors"
              >
                Learn about AI Executive
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Video - Right (bleeds out 15%) */}
            <div className="relative lg:w-[115%]">
              <div className="relative rounded-xl overflow-hidden bg-[#0a0a0a] border border-white/10 shadow-2xl shadow-violet-500/10">
                {/* Video Container */}
                <div className="aspect-video bg-gradient-to-br from-[#111] to-[#0a0a0a] flex items-center justify-center">
                  {/* Mux Video Placeholder */}
                  {MUX_VIDEOS.FEWER_MEETINGS ? (
                    <MuxPlayer
                      data-testid="video-fewer-meetings"
                      playbackId={MUX_VIDEOS.FEWER_MEETINGS}
                      metadata={{
                        video_title: "Executive AI Demo",
                        viewer_user_id: "landing-page-visitor"
                      }}
                      streamType="on-demand"
                      autoPlay="muted"
                      loop
                      muted
                      style={{
                        width: '100%',
                        height: '100%',
                        '--controls': 'none',
                        '--media-object-fit': 'cover'
                      }}
                    />
                  ) : (
                    <div 
                      data-testid="video-placeholder-meetings"
                      className="w-full h-full flex flex-col items-center justify-center text-gray-500"
                    >
                      <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                      <p className="text-sm font-mono">MUX_VIDEO_MEETINGS</p>
                      <p className="text-xs text-gray-600 mt-1">Executive AI Demo</p>
                    </div>
                  )}
                </div>
              </div>
              {/* Subtle glow effect */}
              <div className="absolute -inset-4 bg-violet-500/5 blur-3xl rounded-full pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* END FEATURE SHOWCASE SECTIONS               */}
      {/* ============================================ */}

      {/* Sprint/Release Planning Section */}
      <section className="py-24 bg-[#030303]">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white">
              Opinionated Defaults. Flexible Realities.
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
              We optimized for the standard flow: Planning a sprint <em className="text-gray-300">is</em> planning a release.
              But we know reality is complex. Whether you ship Continuous (CD), Weekly, or on a Fixed Interval, 
              StackRanked adapts. Work that misses the cut slips automatically, giving you honest metrics 
              without the manual data entry.
            </p>
            <Link 
              to="/features#release-sprints"
              className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-medium transition-colors mt-4"
            >
              Learn about Release & Sprints
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Image */}
          <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-purple-500/10">
            <img 
              src="https://customer-assets.emergentagent.com/job_oauth-billing-fix/artifacts/ved71bga_SprintRelease.jpg"
              alt="Sprint and Release Planning Flow"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Still Have Questions — AI Chat CTA */}
      <section className="py-24 relative border-t border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/[0.03] to-transparent pointer-events-none" />
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-purple-500/20 bg-purple-500/5 text-purple-400 text-xs font-medium mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            AI-Powered
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight">
            Still Have Questions?
            <span className="block text-gray-500 text-lg sm:text-xl font-normal mt-2">Just ask - our AI knows every feature inside and out.</span>
          </h2>
          <div className="mt-8 relative max-w-2xl mx-auto group">
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/20 via-pink-500/20 to-purple-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative">
              <input
                type="text"
                data-testid="homepage-ask-input"
                value={askInput}
                onChange={(e) => setAskInput(e.target.value)}
                placeholder="How does StackRanked help product teams?"
                className="w-full bg-white/[0.04] border border-white/10 rounded-2xl pl-6 pr-16 py-5 text-lg text-white placeholder-gray-600 focus:outline-none focus:border-purple-500/40 focus:ring-1 focus:ring-purple-500/20 transition-all"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && askInput.trim()) {
                    const sid = `pub_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
                    localStorage.setItem('sr_ask_session', sid);
                    sessionStorage.setItem('sr_ask_question', askInput.trim());
                    navigate('/ask');
                  }
                }}
              />
              <button
                data-testid="homepage-ask-submit"
                onClick={() => {
                  if (askInput.trim()) {
                    const sid = `pub_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
                    localStorage.setItem('sr_ask_session', sid);
                    sessionStorage.setItem('sr_ask_question', askInput.trim());
                    navigate('/ask');
                  }
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-purple-600 hover:bg-purple-500 flex items-center justify-center cursor-pointer transition-colors"
              >
                <ArrowRight className="w-4 h-4 text-white" />
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-3">Type a question and press Enter or click the arrow</p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <div id="pricing" className="border-t border-white/5">
        <PricingSection safeMode={false} />
      </div>

      {/* Demo CTA Section - Only shown when demo_enabled flag is on */}
      {demoEnabled && (
        <section className="py-32 relative border-t border-white/5 bg-[#020202]" id="demo">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6 text-white">
              Prove It To Yourself.
            </h2>
            <p className="text-gray-400 mb-10 text-lg">
              We built "Twitter for Cats" (Purrview) as a sandbox.
              <br />
              Log in, break it, and see how the AI handles a real product.
            </p>
            
            <button 
              onClick={() => navigate('/login')}
              className="group relative inline-flex items-center justify-center rounded bg-white px-8 py-4 text-base font-medium text-black transition-all hover:bg-gray-200"
            >
              <span className="mr-3 font-mono text-lg">&gt;</span>
              <span className="mr-4">Initialize Demo Environment</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            
            <div className="mt-4 flex justify-center gap-4 text-[10px] text-gray-500 font-mono uppercase tracking-widest">
              <span>No Credit Card</span>
              <span>•</span>
              <span>Instant Access</span>
            </div>
          </div>
        </section>
      )}

      {/* Minimal Footer */}
      <footer className="py-12 px-6 border-t border-white/5 bg-black">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">SR</span>
            </div>
            <span className="font-semibold text-white">StackRanked.ai</span>
          </div>

          {/* Links */}
          <div className="flex items-center gap-6">
            <a 
              href="/terms" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              Terms of Service
            </a>
            <a 
              href="/privacy" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
            >
              Privacy Policy
            </a>
            <a 
              href="https://x.com/Stackrankedai" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-300 transition-colors"
              aria-label="Follow us on X"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
          </div>

          {/* Copyright */}
          <p className="text-sm text-gray-600">
            © 2025 StackRanked.ai - Love Your Customers
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
